import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { publicClient, jwtClient } from './trpc'
import { writeSsoIdToken, readSsoIdToken, clearSsoIdToken, isJwtExpired } from './auth/cookieStorage'

const ID_TOKEN_KEY = 'wa_id_token'
const REFRESH_TOKEN_KEY = 'wa_refresh_token'

export interface AccountProfile {
  accountId: string
  email: string
  provider: string
  name: string | null
  avatar: string | null
}

interface SessionState {
  account: AccountProfile | null
  token: string | null
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  completeFederated: (code: string, codeVerifier: string, redirectUri: string) => Promise<void>
  logout: () => void
  refreshAccount: () => Promise<void>
}

const SessionContext = createContext<SessionState | null>(null)

export function useSession() {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSession must be used within SessionProvider')
  return ctx
}

function persistTokens(idToken: string, refreshToken: string | null) {
  localStorage.setItem(ID_TOKEN_KEY, idToken)
  if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  writeSsoIdToken(idToken)
}

function clearTokens() {
  localStorage.removeItem(ID_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  clearSsoIdToken()
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [account, setAccount] = useState<AccountProfile | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const bootstrapped = useRef(false)

  const establish = useCallback(async (idToken: string) => {
    const session = await jwtClient(idToken).establishSession.mutate()
    const profile: AccountProfile = {
      accountId: session.accountId,
      email: session.email ?? session.profile?.email ?? '',
      provider: session.provider ?? 'email_password',
      name: session.profile?.name ?? null,
      avatar: session.profile?.avatar ?? null,
    }
    setToken(idToken)
    setAccount(profile)
  }, [])

  const adoptToken = useCallback(async (idToken: string, refreshToken: string | null) => {
    persistTokens(idToken, refreshToken)
    await establish(idToken)
  }, [establish])

  const login = useCallback(async (email: string, password: string) => {
    const tokens = await publicClient.login.mutate({ email, password })
    await adoptToken(tokens.idToken, tokens.refreshToken)
  }, [adoptToken])

  const completeFederated = useCallback(async (code: string, codeVerifier: string, redirectUri: string) => {
    const tokens = await publicClient.federatedCallback.mutate({ code, codeVerifier, redirectUri })
    await adoptToken(tokens.idToken, tokens.refreshToken)
  }, [adoptToken])

  const logout = useCallback(() => {
    clearTokens()
    setToken(null)
    setAccount(null)
  }, [])

  const refreshAccount = useCallback(async () => {
    const idToken = localStorage.getItem(ID_TOKEN_KEY)
    if (idToken && !isJwtExpired(idToken)) await establish(idToken)
  }, [establish])

  // Bootstrap: adopt a live session if we have one — a non-expired local token, else a refresh-token
  // renewal (the portal owns refresh), else the cross-app bridge cookie.
  useEffect(() => {
    if (bootstrapped.current) return
    bootstrapped.current = true
    void (async () => {
      try {
        const localId = localStorage.getItem(ID_TOKEN_KEY)
        if (localId && !isJwtExpired(localId)) {
          await establish(localId)
          return
        }
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
        if (refreshToken) {
          try {
            const tokens = await publicClient.refresh.mutate({ refreshToken })
            await adoptToken(tokens.idToken, tokens.refreshToken ?? refreshToken)
            return
          } catch {
            clearTokens()
          }
        }
        const bridged = readSsoIdToken()
        if (bridged) {
          await adoptToken(bridged, null)
          return
        }
      } catch {
        clearTokens()
      } finally {
        setIsLoading(false)
      }
    })()
  }, [establish, adoptToken])

  const value = useMemo<SessionState>(
    () => ({ account, token, isLoading, login, completeFederated, logout, refreshAccount }),
    [account, token, isLoading, login, completeFederated, logout, refreshAccount],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}
